import { get, isEmpty, merge, orderBy, reduce } from 'lodash';
import Converters, { CloudFlexConverters } from '../device-details-component/converters';
import type { DeviceConfiguration, Dictionary, AdditionalInformation } from './types';
import { nestedOmit } from '@lifepowr/components/src/utils';
import type { JSONSchema4 } from 'json-schema';
import { deepClone } from '@mui/x-data-grid/utils/utils';

type ConverterConfig = {
  maxACDCPower: number;
  maxMPPTPower: number;
  maxBatteryChargePower: number;
  maxBatteryDischargePower: number;
};

export type ConverterDto = {
  brand: string;
  model: string;
};

export type DeviceFormConfiguration = {
  address: string;
  addressObj?: any;
  reference: string;
  converter: ConverterDto;
};

export type DeviceSettings = any;

export type FormConfigurationDto = {
  settings: DeviceSettings;
  configuration: DeviceFormConfiguration;
};

function getConvertersList(isCfuDevice: boolean): Record<string, Record<string, ConverterConfig>> {
  if (isCfuDevice) {
    return CloudFlexConverters;
  }

  return Converters;
}

export function setBatteryDefinitionList(batterySchema: JSONSchema4, batteryDefinitionList: JSONSchema4[]): void {
  if (batterySchema.oneOf !== undefined) {
    batterySchema.oneOf = batteryDefinitionList;
  } else if (batterySchema.anyOf !== undefined) {
    batterySchema.anyOf = batteryDefinitionList;
  }
}

export function getBatteryDefinitionList(batterySchema: JSONSchema4): JSONSchema4[] {
  return batterySchema.oneOf ?? batterySchema.anyOf ?? [];
}

function generateBatteryBrandModelNames(batteryConfig: any) {
  const batteryTitle = batteryConfig.title;
  const [brand, ...rest] = batteryTitle.split(' ');
  const model = rest.join(' ');
  return { brand, model };
}

/**
 * Generates a key/value map where key is either the `batteryType.default` or the `title` of the schema definition
 */
export function generateBatterySchemaMap(schemas: any) {
  const batteryDefinitionList = getBatteryDefinitionList(schemas.definitions.Converter.properties.battery);

  return reduce(
    batteryDefinitionList,
    (acc: any, { $ref }) => {
      if (!$ref) {
        return acc;
      }

      const [__, batteryKey] = $ref.split('#/definitions/');
      const batteryConfig = schemas.definitions[batteryKey];
      const batteryReference = get(batteryConfig, 'properties.batteryType.default') || batteryConfig.title;
      acc[batteryReference] = batteryConfig;
      return acc;
    },
    {},
  );
}

function makeAllNonConstBatteryFieldsMandatory(batteryConfig: JSONSchema4) {
  const properties = batteryConfig.properties;
  if (!properties) {
    return;
  }

  batteryConfig.required = Object.entries(properties)
    .filter(([_, fieldSchemaOptions]) => fieldSchemaOptions.const === undefined)
    .map(([fieldName, _]) => {
      return fieldName;
    });
}

/**
 * Returns a map between the battery titles and the battery definition names
 */
export function generateBatteryDefinitionSchemaMap(schemas: any): Dictionary<string> {
  const batteryDefinitionList = getBatteryDefinitionList(schemas.definitions.Converter.properties.battery);

  return reduce(
    batteryDefinitionList,
    (acc: any, { $ref }) => {
      if (!$ref) {
        return acc;
      }

      const [__, batteryKey] = $ref.split('#/definitions/');
      const batteryConfig = schemas.definitions[batteryKey];

      const batteryTitle = get(batteryConfig, 'properties.batteryType.default') || batteryConfig.title;

      makeAllNonConstBatteryFieldsMandatory(batteryConfig);
      acc[batteryTitle] = batteryKey;

      return acc;
    },
    {},
  );
}

export function generateBatteryDataFromBatteryType(_settingsBatteryType: any, schemas: any) {
  let settingsBatteryType = _settingsBatteryType;
  if (settingsBatteryType === 'None') {
    // Start with just a prefill to avoid a crash on the form
    settingsBatteryType = 'GoodWeLXU';
  }
  const originalBatterySchemaMap = generateBatterySchemaMap(schemas);
  const batteryConfig = originalBatterySchemaMap[settingsBatteryType];
  if (!settingsBatteryType || !batteryConfig) {
    return { brand: '', model: '' };
  }
  return generateBatteryBrandModelNames(batteryConfig);
}

export function getBatteryDataOrDefault(batteryData: any, schemas: any) {
  if (batteryData.batteryType === 'None') {
    const batteryDefinition = getBatteryDefinitionFromBrandModel(schemas, {
      brand: 'GoodWe',
      model: 'Lynx Home U Series',
    });

    return getBatteryDefaultDetails(batteryDefinition);
  }

  return batteryData;
}

export function generateBatterySchema(settings: any, schemas: any, preSelectedBattery?: any) {
  const battery: any = {
    type: 'object',
    title: 'Battery Brand',
    oneOf: [],
  };

  const batteryBrandObj = preSelectedBattery ?? generateBatteryDataFromBatteryType(settings?.converters[0].battery?.batteryType, schemas);
  const { brand: selectedBrand, model: selectedModel } = batteryBrandObj;

  const batteryDefinitionList = getBatteryDefinitionList(schemas.definitions.Converter.properties.battery);

  const originalBatterySchema = reduce(
    batteryDefinitionList,
    (acc: any, { $ref }) => {
      if (!$ref) {
        return acc;
      }

      const [__, batteryKey] = $ref.split('#/definitions/');
      const batteryConfig = schemas.definitions[batteryKey];

      const { brand, model } = generateBatteryBrandModelNames(batteryConfig);

      if (['NoBattery', 'UnsupportedBattery', 'LifepowrBattery'].includes(batteryKey)) {
        return acc;
      }

      acc[brand] = acc[brand] ? orderBy([...acc[brand], model]) : [model];

      return acc;
    },
    {},
  );

  orderBy(Object.entries(originalBatterySchema), ([brand]) => brand).forEach(([brand, models]: [string, any]) => {
    const item: any = {
      type: 'object',
      title: brand,
      properties: {
        brand: {
          type: 'string',
          default: brand,
          const: brand,
        },
        model: {
          type: 'string',
          enum: models,
        },
      },
      additionalProperties: false,
      default: {},
    };

    if (models.length >= 1) {
      item.properties.model.default = models[0];
    }

    battery.oneOf.push(item);
  });

  const batteryDetailsSchema = getBatteryDefinitionFromBrandModel(schemas, {
    brand: selectedBrand,
    model: selectedModel,
  });

  return {
    batteryBrandModelSchema: battery,
    batteryDetailsSchema,
  };
}

export function getBatteryDefinitionFromBrandModel(schema: any, { brand, model }: any) {
  if (!brand || !model) {
    return null;
  }
  const title = `${brand} ${model}`;
  return Object.entries(schema.definitions).find(([_, value]: any) => value.title?.includes(title));
}

export function getBatteryDefaultDetails(batteryDefinition: any) {
  const [__, { properties }] = batteryDefinition;
  const battery = Object.entries(properties).reduce((acc: any, [key, value]: any) => {
    acc[key] = value.default;
    return acc;
  }, {});
  return isEmpty(battery) ? null : battery;
}

/**
 * Generate battery settings from a selected brand/model
 */
export function getBatteryDetailsByBrandModel(schema: any, batteryBrandModel: any) {
  const batteryDefinition = getBatteryDefinitionFromBrandModel(schema, batteryBrandModel);
  if (!batteryDefinition) {
    return null;
  }
  return getBatteryDefaultDetails(batteryDefinition);
}

export function getConverterDetailsByBrandModel({ brand: brandName, model: modelName }: ConverterDto, isCfuDevice: boolean = false) {
  const models = getConvertersList(isCfuDevice)[brandName] || {};
  return models[modelName] || {};
}

export function generateConverterSchema(configuration: DeviceConfiguration, isCfuDevice: boolean = false) {
  const converter = {
    $id: 'converterSchema',
    type: 'object',
    title: 'Converter Brand',
    oneOf: [
      {
        type: 'object',
        title: 'No Inverter',
        properties: {
          brand: {
            type: 'string',
            default: 'DefaultInverter',
            const: 'DefaultInverter',
          },
          model: {
            type: 'string',
            enum: ['Custom'],
            enumNames: ['Custom'],
          },
        },
      },
    ],
  };

  if (!configuration) {
    return converter;
  }

  const { converter: selectedConverter = '' } = configuration;
  const [selectedBrand, ...rest] = selectedConverter.split(' ');
  const selectedModel = rest.join(' ');

  Object.entries(getConvertersList(isCfuDevice)).forEach(([brand, models]) => {
    let firstOne = false;
    let firstBrand = false;
    const conv: any = {
      type: 'object',
      title: brand,
      properties: {
        brand: {
          type: 'string',
          default: brand,
          const: brand,
        },
        model: {
          type: 'string',
          enum: [],
          // The labels stay positional with the enum of this branch, so RJSF reads them from
          // the branch that it renders. Do not move them to a ui:enumNames.
          enumNames: [],
        },
      },
    };
    const adjModels = [['Custom', {}], ...Object.entries(models)] as [string, any][];

    adjModels.forEach(([model, modelConfig]: [string, any]) => {
      conv.properties.model.enum.push(model);
      conv.properties.model.enumNames.push(modelConfig?.isBeta ? `[Beta] ${model}` : model);
      if (brand === selectedBrand) {
        firstBrand = true;
        if (model === selectedModel) {
          firstOne = true;
        }
      }
      if (!conv.properties.model.default) {
        conv.properties.model.default = model;
      }
    });

    if (firstOne || firstBrand) {
      converter.oneOf.unshift(conv);
    } else {
      converter.oneOf.push(conv);
    }
  });

  return converter;
}

export function updateConvertersData(newFormConfig: any, formData: any) {
  const brand = get(newFormConfig, 'configuration.converter.brand');
  const model = get(newFormConfig, 'configuration.converter.model');
  const brandExisting = get(formData, 'configuration.converter.brand');
  const modelExisting = get(formData, 'configuration.converter.model');
  const existingConverter = get(formData, 'settings.converters[0]');
  const representation = get(Converters, `${brand}.${model}`);
  const converterDataHasChanged = brand !== brandExisting || model !== modelExisting;

  if (converterDataHasChanged) {
    return merge(newFormConfig, {
      settings: {
        converters: [representation || existingConverter],
      },
    });
  }

  return newFormConfig;
}

export function generateConverterData(configuration: DeviceConfiguration, isCfuDevice: boolean = false): ConverterDto {
  let { converter = 'DefaultInverter' } = configuration;

  if (converter.includes('undefined')) {
    converter = 'DefaultInverter';
  }

  const [converterBrand, ...rest] = converter.split(' ');
  let converterModel = rest.join(' ');

  const { [converterBrand]: brandObj } = getConvertersList(isCfuDevice);
  if (brandObj && !(converterModel in brandObj) && converterModel !== 'Custom') {
    converterModel = Object.keys(brandObj)[0];
  }

  return {
    brand: converterBrand,
    model: converterModel,
  };
}

export function generateNewFormConfig(
  configuration: DeviceConfiguration,
  settings: DeviceSettings,
  additionalInformation: AdditionalInformation,
  isCfuDevice: boolean = false,
): FormConfigurationDto {
  const { address = '', addressObj, reference = '' } = configuration;

  const converterDto = generateConverterData(configuration, isCfuDevice);

  // Because we manipulate setting below, we first need to deepcopy it, so we don't
  // have unexpected side effects.
  const formSettings = deepClone(settings);

  // We need to move unsupportedBatteryDescription from configuration to settings, so we can show
  // it in the form in the right spot.
  if (additionalInformation.unsupportedBatteryDescription) {
    formSettings.converters[0].battery.batteryDescription = additionalInformation.unsupportedBatteryDescription;
  }

  // For Wallonia region in Belgium, populate dsoTariffDropdown from existing dsoTariff METADATA
  const isWallonia = formSettings?.countryStructure?.countryName === 'Belgium' && formSettings?.countryStructure?.regionStructure?.regionName === 'Wallonia';
  if (isWallonia && formSettings.countryStructure?.regionStructure?.segmentStructure) {
    const dsoTariffMetadata = formSettings.countryStructure.regionStructure.segmentStructure.dsoTariff?.METADATA?.internalName;
    if (dsoTariffMetadata) {
      formSettings.countryStructure.regionStructure.segmentStructure.dsoTariffDropdown = dsoTariffMetadata;
    }
  }

  return {
    settings: nestedOmit(formSettings || {}),
    configuration: {
      address,
      addressObj,
      converter: converterDto,
      reference,
    },
  };
}

export const isInvertorHybrid = ({ brand, model }: ConverterDto, isCfuDevice: boolean = false) => {
  const converterBrand = getConvertersList(isCfuDevice)[brand] || {};
  const invertor = converterBrand[model];
  return invertor?.maxMPPTPower > 0;
};

export function generateTariffStructure(schemas: any, settings: any): any {
  if ('tariffStructure' in schemas.properties) {
    return {
      tariffStructure: settings.tariffStructure || {},
    };
  } else {
    return {
      countryStructure: settings.countryStructure || {
        countryName: 'Custom',
        genericDistributionTax: 0.0,
        monthlyCapacityTariff: 0.0,
        tariffStructure: {
          tariffType: 'None',
        },
        customInjectionTariffStructure: [],
      },
    };
  }
}

export function isTariffCountryNameValid(configurationCountryName: string, tariffCountryName: string | undefined): boolean {
  if (!tariffCountryName) {
    return true;
  }

  if (configurationCountryName) {
    return configurationCountryName === tariffCountryName || tariffCountryName.toUpperCase() === 'CUSTOM';
  } else {
    return true;
  }
}
